/**
 * Leva o parceiro das rotas de indicar, de acompanhar indicações e de saldo
 * (Ganhos e Resgatar) do Sapron para a tela equivalente do App Seazone
 * (hostzone).
 *
 * Mora dentro do `RequireAuth` de `/parceiros`: quem chega aqui já está logado
 * no Sapron. A rota sai da tabela única (`rotasParaHostzone.ts`); este
 * componente só decide SE redireciona, com tudo junto:
 *
 * - flags do PostHog respondidas e a da linha da rota ligada
 *   (`ff_redirect_hostzone_indicacoes` ou `ff_redirect_hostzone_saldo`, a
 *   tabela diz qual). Cada uma é o kill-switch da sua superfície: desligar
 *   devolve aquelas rotas ao Sapron como eram, sem deploy;
 * - papel `Partner` e fora do modo de visualização (quem está vendo a conta de
 *   outro não tem essa perspectiva no App Seazone);
 * - fora de moldura: o portal do franqueado embute estas rotas, e o App Seazone
 *   não pode ser carregado em iframe;
 * - host do Sapron conhecido e rota da tabela.
 *
 * Enquanto o PostHog não responde, a tela do Sapron abre: com as flags
 * desligadas, que é o padrão, esperar só atrasaria a tela à toa.
 *
 * A exceção são as rotas cuja tela do Sapron troca de endereço na primeira
 * renderização, antes das flags; trocado o endereço, o redirect sairia da rota
 * errada:
 * - o índice de indicações (`/parceiros/indicacoes`), um `<Navigate>` para a
 *   aba Locação: o parceiro cairia no quadro do app filtrado em Locação, e não
 *   no quadro inteiro;
 * - Ganhos e Resgatar para o parceiro não titular: lá a tela do Sapron é o
 *   `RequireAccountOwner`, que o manda ao painel, e do painel ele nunca mais
 *   chegaria ao app.
 *
 * Nelas os filhos esperam o PostHog, no máximo `ESPERA_MAXIMA_MS`; vencida a
 * espera, ou com a flag da rota desligada, segue como hoje.
 */
import { type ReactElement, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useUser } from '@/context/UserContext';
import { useViewMode } from '@/context/ViewMode/ViewMode';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';

import { destinoNoHostzone } from './rotasParaHostzone';

import { dentroDeMoldura } from '@/lib/moldura';

interface IRedirecionaParaHostzone {
  children: ReactElement;
}

/** Quanto a rota que troca de endereço espera o PostHog antes de seguir como hoje. */
export const ESPERA_MAXIMA_MS = 5000;

const SO_SALDO: ReadonlySet<FeatureFlagsOptions> = new Set([
  FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_SALDO,
]);

const SO_INDICACOES: ReadonlySet<FeatureFlagsOptions> = new Set([
  FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_INDICACOES,
]);

export const RedirecionaParaHostzone = ({ children }: IRedirecionaParaHostzone) => {
  const { pathname } = useLocation();
  const { featureFlags, loading } = useFeatureFlags();
  const { userInformation } = useUser();
  const { isViewMode } = useViewMode();

  // Durante a carga o mapa nasce todo `false`: nenhuma flag conta como ligada.
  const flagsLigadas = new Set(
    loading ? [] : Object.values(FeatureFlagsOptions).filter((flag) => featureFlags[flag] === true),
  );
  const ehParceiro = userInformation?.roles?.includes('Partner') === true;
  const podeRedirecionar = ehParceiro && !isViewMode && !dentroDeMoldura();
  const { hostname } = window.location;

  const destino = podeRedirecionar ? destinoNoHostzone(pathname, hostname, flagsLigadas) : null;

  // Rota de saldo = a que iria ao app se a flag de saldo estivesse ligada.
  const naoTitularNoSaldo =
    userInformation?.partner?.is_account_owner === false &&
    destinoNoHostzone(pathname, hostname, SO_SALDO) !== null;
  const indiceDeIndicacoes =
    matchPath('/parceiros/indicacoes', pathname) !== null &&
    destinoNoHostzone(pathname, hostname, SO_INDICACOES) !== null;
  const esperaOPostHog = loading && podeRedirecionar && (naoTitularNoSaldo || indiceDeIndicacoes);
  const [esperaVencida, setEsperaVencida] = useState(false);

  useEffect(() => {
    if (!esperaOPostHog) return undefined;
    const relogio = setTimeout(() => setEsperaVencida(true), ESPERA_MAXIMA_MS);
    return () => clearTimeout(relogio);
  }, [esperaOPostHog]);

  useEffect(() => {
    if (destino) window.location.replace(destino);
  }, [destino]);

  return destino || (esperaOPostHog && !esperaVencida) ? null : children;
};

export default RedirecionaParaHostzone;
